import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, BACKEND_URL, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PortalLayout from "../components/PortalLayout";
import DocumentPreview from "../components/DocumentPreview";
import {
  User,
  FileText,
  BookOpen,
  Upload,
  Download,
  Eye,
  MessageSquare,
  Send,
  Plus,
  Settings,
  Users,
  File,
  X,
  AlertCircle,
  ChevronDown,
  CheckCircle2,
  Clock,
} from "lucide-react";

const TeamClientDetail = () => {
  const { clientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  // Document upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadService, setUploadService] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadFY, setUploadFY] = useState("");
  const [uploadProfileId, setUploadProfileId] = useState("");

  // Service info state (per-profile)
  const [serviceInfos, setServiceInfos] = useState([]);
  const [selectedServiceProfile, setSelectedServiceProfile] = useState("__default__");
  const [dueDate, setDueDate] = useState("");
  const [pendingItems, setPendingItems] = useState([]);
  const [newPendingItem, setNewPendingItem] = useState("");
  const [savingInfo, setSavingInfo] = useState(false);
  const [workStatus, setWorkStatus] = useState("in_progress");
  const [markingComplete, setMarkingComplete] = useState(false);

  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // FY filter
  const [selectedFY, setSelectedFY] = useState("all");

  // Preview
  const [previewDoc, setPreviewDoc] = useState(null);

  // Export loading
  const [exporting, setExporting] = useState(false);

  const fyOptions = ["2025-26", "2024-25", "2023-24", "2022-23"];

  const fetchClientInfo = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/team/client/${clientId}/info`);
      if (res.ok) {
        const data = await res.json();
        setClientInfo(data);
        const infos = data.service_infos || [];
        setServiceInfos(infos);
        // Load the first/default profile's service info
        const defaultInfo = infos.find((i) => i.profile_id === "__default__") || infos[0] || {};
        setDueDate(defaultInfo.filing_due_date || "");
        setPendingItems(defaultInfo.pending_items || []);
        setWorkStatus(defaultInfo.work_status || "in_progress");
      } else {
        toast.error("Failed to load client info");
        navigate("/team");
      }
    } catch {
      toast.error("Error loading client");
      navigate("/team");
    }
  }, [clientId, navigate]);

  const fetchDocuments = useCallback(async () => {
    const res = await authFetch(`${API}/team/client/${clientId}/documents`);
    if (res.ok) setDocuments(await res.json());
  }, [clientId]);

  const fetchTransactions = useCallback(async () => {
    const fyParam = selectedFY !== "all" ? `?fy=${selectedFY}` : "";
    const res = await authFetch(`${API}/team/client/${clientId}/transactions${fyParam}`);
    if (res.ok) setTransactions(await res.json());
  }, [clientId, selectedFY]);

  const fetchProfiles = useCallback(async () => {
    const res = await authFetch(`${API}/team/client/${clientId}/profiles`);
    if (res.ok) setProfiles(await res.json());
  }, [clientId]);

  const fetchMessages = useCallback(async () => {
    if (!clientId || !user) return;
    const participants = [user.user_id, clientId].sort();
    const threadId = `thread_${participants[0]}:::${participants[1]}`;
    try {
      const res = await authFetch(`${API}/messages/thread/${threadId}`);
      if (res.ok) setMessages(await res.json());
    } catch {}
  }, [clientId, user]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchClientInfo(), fetchDocuments(), fetchTransactions(), fetchProfiles()]);
      setLoading(false);
    };
    init();
  }, [fetchClientInfo, fetchDocuments, fetchTransactions, fetchProfiles]);

  useEffect(() => {
    fetchTransactions();
  }, [selectedFY, fetchTransactions]);

  useEffect(() => {
    if (activeTab === "messages") {
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeTab, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // When selectedServiceProfile changes, load that profile's info
  useEffect(() => {
    const info = serviceInfos.find((i) => i.profile_id === selectedServiceProfile);
    setDueDate(info?.filing_due_date || "");
    setPendingItems(info?.pending_items || []);
    setWorkStatus(info?.work_status || "in_progress");
  }, [selectedServiceProfile, serviceInfos]);

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadCategory || !uploadService) {
      toast.error("Please fill all required fields");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("document_category", uploadCategory);
      formData.append("service_type", uploadService);
      if (uploadNotes) formData.append("notes", uploadNotes);
      if (uploadFY) formData.append("financial_year", uploadFY);
      if (uploadProfileId) formData.append("profile_id", uploadProfileId);

      const res = await authFetch(`${API}/team/client/${clientId}/upload-document`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        toast.success("Document uploaded for client");
        setUploadOpen(false);
        setUploadFile(null);
        setUploadCategory("");
        setUploadService("");
        setUploadNotes("");
        setUploadFY("");
        setUploadProfileId("");
        fetchDocuments();
        fetchClientInfo();
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveServiceInfo = async () => {
    setSavingInfo(true);
    try {
      const res = await authFetch(`${API}/team/client/${clientId}/service-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_id: selectedServiceProfile,
          filing_due_date: dueDate || null,
          pending_items: pendingItems,
          work_status: workStatus,
        }),
      });
      if (res.ok) {
        toast.success("Service info updated");
        fetchClientInfo();
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Error saving");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleToggleWorkStatus = async () => {
    const newStatus = workStatus === "completed" ? "in_progress" : "completed";
    setMarkingComplete(true);
    try {
      const res = await authFetch(`${API}/team/client/${clientId}/service-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_id: selectedServiceProfile,
          filing_due_date: dueDate || null,
          pending_items: pendingItems,
          work_status: newStatus,
        }),
      });
      if (res.ok) {
        setWorkStatus(newStatus);
        toast.success(newStatus === "completed" ? "✅ Work marked as complete! Client notified." : "Work reopened.");
        fetchClientInfo();
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("Error updating status");
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await authFetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_id: clientId, content: newMessage.trim() }),
      });
      if (res.ok) {
        setNewMessage("");
        fetchMessages();
      } else {
        toast.error("Failed to send");
      }
    } catch {
      toast.error("Send error");
    } finally {
      setSending(false);
    }
  };

  const handleExportTransactions = async () => {
    setExporting(true);
    try {
      const fyParam = selectedFY !== "all" ? `?fy=${selectedFY}` : "";
      const res = await authFetch(`${API}/team/client/${clientId}/transactions/export${fyParam}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = res.headers.get("content-disposition");
        const filename = disposition?.match(/filename="(.+)"/)?.[1] || "transactions.xlsx";
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      } else {
        toast.error("Export failed");
      }
    } catch {
      toast.error("Export error");
    } finally {
      setExporting(false);
    }
  };

  const addPendingItem = () => {
    if (newPendingItem.trim()) {
      setPendingItems([...pendingItems, newPendingItem.trim()]);
      setNewPendingItem("");
    }
  };

  const removePendingItem = (index) => {
    setPendingItems(pendingItems.filter((_, i) => i !== index));
  };

  const getFileViewUrl = (doc) => {
    const token = localStorage.getItem("session_token");
    if (doc.file_id) return `${BACKEND_URL}/api/files/${doc.file_id}?token=${token}`;
    if (doc.file_url) return `${BACKEND_URL}${doc.file_url}${doc.file_url.includes("?") ? "&" : "?"}token=${token}`;
    return null;
  };

  const getProfileName = (profileId) => {
    if (!profileId) return null;
    const p = profiles.find((pr) => pr.profile_id === profileId);
    return p ? p.full_name : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  const client = clientInfo?.client;
  const payment = clientInfo?.payment;
  const serviceType = payment?.service_type;

  const teamDocCategories = [
    { value: "itr_acknowledgment", label: "ITR Acknowledgment" },
    { value: "tax_computation", label: "Tax Computation Sheet" },
    { value: "final_return", label: "Final Return Copy" },
    { value: "assessment_order", label: "Assessment Order" },
    { value: "gst_return", label: "GST Return" },
    { value: "balance_sheet", label: "Balance Sheet" },
    { value: "profit_loss", label: "Profit & Loss Statement" },
    { value: "other", label: "Other" },
  ];

  // Build profile options for service info (includes a "General" default)
  const serviceProfileOptions = [
    { id: "__default__", name: "General (All Profiles)" },
    ...profiles.map((p) => ({ id: p.profile_id, name: p.full_name })),
  ];

  return (
    <PortalLayout title="Client Detail">

      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* Client Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="soft-card mb-3"
          data-testid="client-header"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {client?.picture ? (
                <img src={client.picture} alt={client?.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[var(--accent)] shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--accent-bg)" }}>
                  <User className="w-7 h-7 text-[var(--accent)]" />
                </div>
              )}
              <div className="min-w-0 overflow-hidden">
                <h1 className="font-['Playfair_Display'] text-xl sm:text-2xl font-bold truncate" style={{ color: "var(--text-primary)" }}>
                  {client?.name}
                </h1>
                <p className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{client?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {serviceType && (
                <span className="px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>
                  {serviceType === "itr_filing" ? "ITR Filing" : serviceType === "accounting" ? "Accounting" : "Combined"}
                </span>
              )}
              {payment?.status === "completed" && (
                <span className="px-3 py-1.5 text-sm font-medium rounded-md bg-[#10B981]/15 text-[#10B981] whitespace-nowrap">
                  Active
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats Row - No financial data */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-3 gap-2 sm:gap-4 mb-3"
        >
          {[
            { label: "Documents", value: clientInfo?.document_count || 0, icon: FileText, color: "var(--accent)" },
            { label: "Transactions", value: clientInfo?.transaction_count || 0, icon: BookOpen, color: "#F59E0B" },
            { label: "Profiles", value: clientInfo?.profile_count || 0, icon: Users, color: "#8B5CF6" },
          ].map((stat) => (
            <div key={stat.label} className="soft-card !p-3 sm:!p-5" data-testid={`stat-${stat.label.toLowerCase()}`}>
              <div className="flex flex-col lg:flex-row items-center gap-2 lg:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${stat.color}15` }}>
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: stat.color }} />
                </div>
                <div className="text-center lg:text-left min-w-0 w-full overflow-hidden">
                  <p className="text-[10px] sm:text-xs truncate block" style={{ color: "var(--text-muted)" }}>{stat.label}</p>
                  <p className="text-sm sm:text-lg font-bold" style={{ color: "var(--text-primary)" }}>{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Tabs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="soft-card !p-1 border-[var(--border-light)] bg-[var(--background-primary)] shadow-sm flex-wrap h-auto justify-start overflow-x-auto whitespace-nowrap max-w-[100vw] sm:max-w-none">
              {[
                { value: "overview", label: "Overview" },
                { value: "documents", label: `Documents (${documents.length})` },
                { value: "transactions", label: `Transactions (${transactions.length})` },
                { value: "service", label: "Service Info" },
                { value: "messages", label: "Messages" },
              ].map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white rounded-md text-xs sm:text-sm px-3 py-1.5"
                  data-testid={`tab-${tab.value}`}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ===================== OVERVIEW TAB ===================== */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-3">
                {/* Service Status */}
                <div className="soft-card" data-testid="overview-service">
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                    <Settings className="w-5 h-5 text-[var(--accent)]" /> Service Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Service Type</span>
                      <span className="font-medium" style={{ color: "var(--accent)" }}>
                        {serviceType === "itr_filing" ? "ITR Filing" : serviceType === "accounting" ? "Accounting" : serviceType === "combined" ? "Combined" : "Not Set"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Status</span>
                      <span className={`px-2 py-0.5 text-xs rounded ${payment?.status === "completed" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
                        {payment?.status === "completed" ? "Active" : "Pending"}
                      </span>
                    </div>
                    {/* Work Completion Status */}
                    {serviceInfos.length > 0 && (
                      <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: "var(--border-color)" }}>
                        <span style={{ color: "var(--text-secondary)" }}>Work Status</span>
                        {(serviceInfos[0]?.work_status || "in_progress") === "completed" ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-[#10B981]/15 text-[#10B981] font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-[#F59E0B]/15 text-[#F59E0B] font-semibold">
                            <Clock className="w-3.5 h-3.5" /> In Progress
                          </span>
                        )}
                      </div>
                    )}
                    {/* Per-profile service info summary */}
                    {serviceInfos.length > 0 && serviceInfos.some((i) => i.filing_due_date || i.pending_items?.length) && (
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border-color)" }}>
                        <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-muted)" }}>FILING INFO</p>
                        {serviceInfos.filter((i) => i.filing_due_date || i.pending_items?.length).map((info) => {
                          const pName = info.profile_id === "__default__" ? "General" : getProfileName(info.profile_id) || info.profile_id;
                          return (
                            <div key={info.profile_id} className="mb-2">
                              <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{pName}</p>
                              {info.filing_due_date && <p className="text-xs" style={{ color: "var(--text-secondary)" }}>Due: {info.filing_due_date}</p>}
                              {info.pending_items?.length > 0 && (
                                <p className="text-xs" style={{ color: "#F59E0B" }}>{info.pending_items.length} pending item(s)</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="soft-card" data-testid="overview-quick-stats">
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                    <FileText className="w-5 h-5 text-[var(--accent)]" /> Quick Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Total Documents</span>
                      <span className="font-medium" style={{ color: "var(--text-primary)" }}>{clientInfo?.document_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Total Transactions</span>
                      <span className="font-medium" style={{ color: "var(--text-primary)" }}>{clientInfo?.transaction_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Profiles</span>
                      <span className="font-medium" style={{ color: "var(--text-primary)" }}>{clientInfo?.profile_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: "var(--text-secondary)" }}>Joined</span>
                      <span style={{ color: "var(--text-primary)" }}>{client?.created_at ? new Date(client.created_at).toLocaleDateString("en-IN") : "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Profiles */}
                <div className="soft-card md:col-span-2" data-testid="overview-profiles">
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                    <Users className="w-5 h-5 text-[var(--accent)]" /> Client Profiles ({profiles.length})
                  </h3>
                  {profiles.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {profiles.map((p) => (
                        <div key={p.profile_id} className="p-4 rounded-lg border" style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
                          <p className="font-medium" style={{ color: "var(--text-primary)" }}>{p.full_name}</p>
                          <div className="mt-2 space-y-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                            {p.pan_number && <p>PAN: {p.pan_number}</p>}
                            {p.aadhar_number && <p>Aadhar: {p.aadhar_number}</p>}
                            {p.date_of_birth && <p>DOB: {p.date_of_birth}</p>}
                          </div>
                          {(p.pan_doc_url || p.aadhar_doc_url) && (
                            <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border-color)" }}>
                              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                                KYC Documents
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {p.pan_doc_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs rounded-md border hover:border-[#145886] hover:bg-[#145886]/5 transition-colors"
                                    style={{ borderColor: "var(--border-color)" }}
                                    onClick={() => setPreviewDoc({
                                      file_url: p.pan_doc_url,
                                      filename: `${p.full_name} - PAN Card`,
                                      content_type: p.pan_doc_url.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image"
                                    })}
                                    data-testid={`preview-pan-${p.profile_id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1 text-[var(--accent)]" /> PAN Card
                                  </Button>
                                )}
                                {p.aadhar_doc_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs rounded-md border hover:border-[#145886] hover:bg-[#145886]/5 transition-colors"
                                    style={{ borderColor: "var(--border-color)" }}
                                    onClick={() => setPreviewDoc({
                                      file_url: p.aadhar_doc_url,
                                      filename: `${p.full_name} - Aadhaar Card`,
                                      content_type: p.aadhar_doc_url.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image"
                                    })}
                                    data-testid={`preview-aadhar-${p.profile_id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1 text-[var(--accent)]" /> Aadhaar Card
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "var(--text-muted)" }}>No profiles created yet</p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ===================== DOCUMENTS TAB ===================== */}
            <TabsContent value="documents">
              <div className="soft-card" data-testid="documents-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2 text-[var(--text-main)]">
                    <FileText className="w-5 h-5 text-[var(--primary)]" /> Documents
                  </h3>
                  <button
                    onClick={() => setUploadOpen(true)}
                    className="soft-btn !px-4 !py-2 text-sm"
                    data-testid="upload-doc-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Upload Document
                  </button>
                </div>

                {documents.length > 0 ? (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Uploaded By</TableHead>
                        <TableHead>FY</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc.document_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 shrink-0" style={{ color: "var(--text-muted)" }} />
                              <span className="font-medium truncate max-w-[180px]" style={{ color: "var(--text-primary)" }}>{doc.filename}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 text-xs rounded" style={{ background: "var(--accent-bg)", color: "var(--text-secondary)" }}>
                              {doc.document_category?.replace(/_/g, " ")}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${doc.uploaded_by === "team" ? "bg-[#8B5CF6]/15 text-[#8B5CF6]" : "bg-[#3B82F6]/15 text-[#3B82F6]"}`}>
                              {doc.uploaded_by === "team" ? "Team" : "Client"}
                            </span>
                          </TableCell>
                          <TableCell style={{ color: "var(--text-secondary)" }}>{doc.financial_year || "-"}</TableCell>
                          <TableCell style={{ color: "var(--text-secondary)" }}>{new Date(doc.uploaded_at).toLocaleDateString("en-IN")}</TableCell>
                          <TableCell className="max-w-[150px] truncate" style={{ color: "var(--text-muted)" }}>{doc.notes || "-"}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                              data-testid={`preview-doc-${doc.document_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                    <p style={{ color: "var(--text-secondary)" }}>No documents yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ===================== TRANSACTIONS TAB ===================== */}
            <TabsContent value="transactions">
              <div className="soft-card" data-testid="transactions-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2 text-[var(--text-main)]">
                    <BookOpen className="w-5 h-5 text-[var(--primary)]" /> Transactions
                  </h3>
                  <div className="flex items-center gap-3">
                    <Select value={selectedFY} onValueChange={setSelectedFY}>
                      <SelectTrigger className="w-36 rounded-md" data-testid="fy-filter">
                        <SelectValue placeholder="All FYs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All FYs</SelectItem>
                        {fyOptions.map((fy) => (
                          <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      onClick={handleExportTransactions}
                      disabled={exporting || transactions.length === 0}
                      className="px-4 py-2 rounded-xl border border-[var(--border-light)] bg-white text-[var(--text-main)] hover:border-[var(--primary)] text-sm font-bold shadow-sm transition-all disabled:opacity-50 flex items-center"
                      data-testid="export-transactions-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {exporting ? "Exporting..." : "Export Excel"}
                    </button>
                  </div>
                </div>

                {transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Payee</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Profile</TableHead>
                          <TableHead className="text-right">GST</TableHead>
                          <TableHead>FY</TableHead>
                          <TableHead>File</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((txn) => (
                          <TableRow key={txn.transaction_id}>
                            <TableCell style={{ color: "var(--text-secondary)" }}>{txn.date}</TableCell>
                            <TableCell style={{ color: "var(--text-secondary)" }}>{txn.invoice_no || "-"}</TableCell>
                            <TableCell className="font-medium" style={{ color: "var(--text-primary)" }}>{txn.payee}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 text-xs rounded ${txn.category === "sales" ? "bg-[#10B981]/15 text-[#10B981]" : txn.category === "purchases" ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                                {txn.category}
                              </span>
                            </TableCell>
                            <TableCell style={{ color: "var(--text-secondary)" }}>{getProfileName(txn.profile_id) || "-"}</TableCell>
                            <TableCell className="text-right" style={{ color: "var(--text-secondary)" }}>
                              {txn.gst_amount ? `${txn.gst_rate}%` : "-"}
                            </TableCell>
                            <TableCell style={{ color: "var(--text-muted)" }}>{txn.financial_year || "-"}</TableCell>
                            <TableCell>
                              {txn.file_url ? (
                                <Button variant="ghost" size="sm" onClick={() => {
                                  const token = localStorage.getItem("session_token");
                                  window.open(`${BACKEND_URL}${txn.file_url}${txn.file_url.includes("?") ? "&" : "?"}token=${token}`, "_blank");
                                }}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              ) : (
                                <span style={{ color: "var(--text-muted)" }}>-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                    <p style={{ color: "var(--text-secondary)" }}>No transactions recorded</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ===================== SERVICE INFO TAB (Per-Profile) ===================== */}
            <TabsContent value="service">
              <div className="soft-card max-w-2xl" data-testid="service-info-section">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-[var(--text-main)]">
                  <Settings className="w-5 h-5 text-[var(--primary)]" /> Manage Service Info
                </h3>

                <div className="space-y-6">
                  {/* Profile Selector */}
                  <div>
                    <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--text-primary)" }}>
                      Select Profile
                    </label>
                    <Select value={selectedServiceProfile} onValueChange={setSelectedServiceProfile}>
                      <SelectTrigger className="max-w-xs rounded-md" data-testid="service-profile-select">
                        <SelectValue placeholder="Select profile" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceProfileOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--text-primary)" }}>
                      Filing Due Date
                    </label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="max-w-xs rounded-xl border-[var(--border-light)]"
                      data-testid="due-date-input"
                    />
                  </div>

                  {/* Pending Items */}
                  <div>
                    <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--text-primary)" }}>
                      Pending Items
                    </label>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={newPendingItem}
                        onChange={(e) => setNewPendingItem(e.target.value)}
                        placeholder="e.g. Upload Form 16"
                        className="rounded-md"
                        data-testid="pending-item-input"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPendingItem(); } }}
                      />
                      <Button type="button" onClick={addPendingItem} variant="outline" className="rounded-md shrink-0" data-testid="add-pending-item-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {pendingItems.length > 0 ? (
                      <div className="space-y-2">
                        {pendingItems.map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 rounded-md border"
                            style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}
                          >
                            <span className="text-sm" style={{ color: "var(--text-primary)" }}>{item}</span>
                            <button onClick={() => removePendingItem(i)} className="text-[#EF4444] hover:bg-[#EF4444]/10 p-1 rounded" data-testid={`remove-pending-${i}`}>
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--text-muted)" }}>No pending items for this profile</p>
                    )}
                  </div>

                  <button
                    onClick={handleSaveServiceInfo}
                    disabled={savingInfo}
                    className="soft-btn disabled:opacity-50"
                    data-testid="save-service-info-btn"
                  >
                    {savingInfo ? "Saving..." : "Save Changes"}
                  </button>

                  {/* Mark as Complete / Reopen */}
                  <div className="pt-4 border-t" style={{ borderColor: "var(--border-color)" }}>
                    <p className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>Work Completion</p>
                    <div className="flex items-center gap-4">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium ${
                        workStatus === "completed"
                          ? "bg-[#10B981]/15 text-[#10B981]"
                          : "bg-[#F59E0B]/15 text-[#F59E0B]"
                      }`}>
                        {workStatus === "completed"
                          ? <><CheckCircle2 className="w-4 h-4" /> Completed</>
                          : <><Clock className="w-4 h-4" /> In Progress</>}
                      </div>
                      <button
                        onClick={handleToggleWorkStatus}
                        disabled={markingComplete}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 ${
                          workStatus === "completed"
                            ? "border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                            : "bg-[#10B981] text-white hover:bg-[#059669]"
                        }`}
                        data-testid="toggle-work-status-btn"
                      >
                        {markingComplete
                          ? "Updating..."
                          : workStatus === "completed"
                          ? "Reopen Work"
                          : "✅ Mark as Complete"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ===================== MESSAGES TAB ===================== */}
            <TabsContent value="messages">
              <div className="soft-card" data-testid="messages-section">
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-[var(--text-main)]">
                  <MessageSquare className="w-5 h-5 text-[var(--primary)]" /> Messages with {client?.name}
                </h3>
                <div
                  className="h-[400px] overflow-y-auto mb-4 p-5 rounded-[20px] border border-[var(--border-light)] bg-[var(--background-primary)] shadow-inner"
                  data-testid="messages-container"
                >
                  {messages.length > 0 ? (
                    <div className="space-y-3">
                      {messages.map((msg) => {
                        const isMe = msg.sender_id === user?.user_id;
                        return (
                          <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[70%] px-4 py-2.5 rounded-xl ${isMe ? "rounded-br-sm" : "rounded-bl-sm"}`}
                              style={{
                                background: isMe ? "var(--accent)" : "var(--bg-secondary)",
                                color: isMe ? "#0F172A" : "var(--text-primary)",
                                border: isMe ? "none" : `1px solid var(--border-color)`,
                              }}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${isMe ? "text-white/60" : ""}`} style={isMe ? {} : { color: "var(--text-muted)" }}>
                                {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p style={{ color: "var(--text-muted)" }}>No messages yet. Start a conversation!</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="rounded-full px-5 border-[var(--border-light)] bg-white shadow-sm flex-1"
                    data-testid="message-input"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="soft-btn !p-0 w-10 h-10 flex items-center justify-center rounded-full shrink-0 disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-lg max-w-md" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']" style={{ color: "var(--text-primary)" }}>
              Upload Document for {client?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>File *</label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="rounded-md"
                data-testid="upload-file-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Category *</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="rounded-md hover:border-[#145886] transition-all cursor-pointer" data-testid="upload-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {teamDocCategories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Service Type *</label>
              <Select value={uploadService} onValueChange={setUploadService}>
                <SelectTrigger className="rounded-md hover:border-[#145886] transition-all cursor-pointer" data-testid="upload-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="itr_filing">ITR Filing</SelectItem>
                  <SelectItem value="accounting">Accounting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Financial Year</label>
              <Select value={uploadFY} onValueChange={setUploadFY}>
                <SelectTrigger className="rounded-md hover:border-[#145886] transition-all cursor-pointer">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map((fy) => (
                    <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {profiles.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Profile</label>
                <Select value={uploadProfileId} onValueChange={setUploadProfileId}>
                  <SelectTrigger className="rounded-md hover:border-[#145886] transition-all cursor-pointer">
                    <SelectValue placeholder="Select profile (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.profile_id} value={p.profile_id}>{p.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Notes</label>
              <Textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Optional notes..."
                className="rounded-md"
                rows={2}
                data-testid="upload-notes-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setUploadOpen(false)} className="rounded-md">Cancel</Button>
              <Button
                onClick={handleUploadDocument}
                disabled={uploading}
                className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md"
                data-testid="confirm-upload-btn"
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview */}
      {previewDoc && (
        <DocumentPreview
          fileUrl={getFileViewUrl(previewDoc)}
          filename={previewDoc.filename}
          contentType={previewDoc.content_type || previewDoc.file_type}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    
    </PortalLayout>
  );
};

export default TeamClientDetail;
